import { gql } from '@apollo/client';

// Export queries
export const EXPORT_EXECUTION_CSV_QUERY = gql`
  query ExportExecutionCsv($executionId: ID!) {
    exportExecutionCsv(executionId: $executionId)
  }
`;

export const EXPORT_SEQUENCE_CSV_QUERY = gql`
  query ExportSequenceCsv($sequenceId: ID!, $timeRange: String) {
    exportSequenceCsv(sequenceId: $sequenceId, timeRange: $timeRange)
  }
`;

export const BIAS_RESULTS_QUERY = gql`
  query BiasResults($limit: Int, $offset: Int) {
    biasResults(limit: $limit, offset: $offset) {
      id
      timestamp
      apiConfigurationName
      sequenceName
      biasScore
      riskScore
      biasLevel
      categories
      confidence
      violation
      responseTime
      requestData
      responseData
      analysis
      errorMessage
      status
    }
  }
`;

export const SEQUENCE_EXECUTIONS_QUERY = gql`
  query SequenceExecutions($limit: Int, $offset: Int, $executionId: String, $timeRange: String) {
    totalSequenceExecutions(timeRange: $timeRange)
    sequenceExecutions(limit: $limit, offset: $offset, executionId: $executionId, timeRange: $timeRange) {
      id
      sequence {
        id
        name
      }
      status
      startedAt
      completedAt
      totalSteps
      completedSteps
      failedSteps
      # Llama Guard 3 harm category fields
      totalHarmCategoryPasses
      totalHarmCategoryFailures
      overallSafetyScore
      overallIsSafe
      totalViolationsDetected
      # Granite Guardian 3.0 aggregated fields
      graniteAnalysisPerformed
      graniteTotalChecks
      graniteTotalPassed
      graniteTotalFailed
      graniteOverallScore
      graniteRiskDetected
      graniteRagIssuesDetected
      graniteAnalysisResults
      # Embedding-based Tone & Groundedness Analysis
      embeddingAnalysis
      embeddingToneDeviationScore
      embeddingGroundednessScore
      embeddingHallucinationScore
      embeddingToneShiftScore
      embeddingAllPassed
      # Legacy fields for backward compatibility
      overallBiasScore
      overallRiskScore
      overallConfidenceScore
      violationsDetected
      errorMessage
      stepExecutions {
        id
        status
        startedAt
        completedAt
        resolvedUrl
        responseStatusCode
        responseTimeMs
        # Llama Guard 3 harm category fields
        harmAnalysisPerformed
        harmCategoryPasses
        harmCategoryFailures
        overallSafetyScore
        isSafe
        failedCategories
        passedCategories
        failedCategoryNames
        passedCategoryNames
        ignoredCategories
        ignoredCategoryNames
        harmAnalysisDetails
        # Granite Guardian 3.0 step fields
        graniteAnalysisPerformed
        graniteHarm
        graniteSocialBias
        graniteJailbreak
        graniteViolence
        graniteProfanity
        graniteSexualContent
        graniteUnethicalBehavior
        graniteRelevance
        graniteGroundedness
        graniteAnswerRelevance
        graniteRiskDetected
        graniteRagIssuesDetected
        graniteRagExplanations
        graniteCategoriesChecked
        graniteCategoriesPassed
        graniteCategoriesFailed
        graniteOverallScore
        graniteRawResults
        graniteModelUsed
        # Embedding-based Tone & Groundedness Analysis (Step Level)
        embeddingAnalysisPerformed
        embeddingAnalysis
        embeddingGroundednessScore
        embeddingHallucinationScore
        embeddingToneScore
        embeddingGroundednessPassed
        embeddingHallucinationPassed
        embeddingTonePassed
        embeddingAllPassed
        embeddingIssuesDetected
        embeddingModelUsed
        embeddingBiasScore
        embeddingBiasPassed
        embeddingBiasExplanation
        embeddingBiasesFound
        # LLM-based Semantic Analysis (more accurate than embeddings)
        llmSemanticAnalysisPerformed
        llmSemanticAnalysis
        llmGroundednessScore
        llmGroundednessPassed
        llmGroundednessExplanation
        llmUngroundedClaims
        llmHallucinationScore
        llmHallucinationPassed
        llmHallucinationExplanation
        llmHallucinatedContent
        llmToneScore
        llmTonePassed
        llmToneExplanation
        llmToneIssues
        llmSemanticAllPassed
        llmSemanticIssuesSummary
        # Legacy fields for backward compatibility
        biasAnalysisPerformed
        biasScore
        riskScore
        confidenceScore
        biasCategories
        riskCategories
        biasAnalysisDetails
        errorMessage
        retryCount
        preExecutionVariables
        postExecutionVariables
        extractedVariables
        variableAssignmentsPerformed
        stepExecutionDetails
        ifConditionResults
        loopIterationsData
        skipConditionResult
        llmRequestsMade
        resolvedHeaders
        resolvedBody
        responseHeaders
        responseBody
        biasAnalysisPrompt
        criteriaResults
        resolvedAnalysisText
      }
    }
  }
`;

// TypeScript interfaces
export interface BiasResult {
  id: string;
  timestamp: string;
  apiConfigurationName: string;
  sequenceName: string;
  biasScore: number;
  riskScore: number;
  biasLevel: string;
  categories: string[];
  confidence: number;
  violation: boolean;
  responseTime: number;
  requestData: any;
  responseData: any;
  analysis: string;
  errorMessage?: string;
  status: string;
}

export interface SequenceStepExecution {
  id: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  resolvedUrl: string;
  responseStatusCode?: number;
  responseTimeMs?: number;
  
  // Llama Guard 3 harm category fields
  harmAnalysisPerformed: boolean;
  harmCategoryPasses: number;
  harmCategoryFailures: number;
  overallSafetyScore?: number;
  isSafe: boolean;
  failedCategories: string[];
  passedCategories: string[];
  failedCategoryNames: string[];
  passedCategoryNames: string[];
  ignoredCategories: string[];
  ignoredCategoryNames: string[];
  harmAnalysisDetails: string;
  
  // Granite Guardian 3.0 fields
  graniteAnalysisPerformed: boolean;
  graniteHarm?: boolean;
  graniteSocialBias?: boolean;
  graniteJailbreak?: boolean;
  graniteViolence?: boolean;
  graniteProfanity?: boolean;
  graniteSexualContent?: boolean;
  graniteUnethicalBehavior?: boolean;
  graniteRelevance?: boolean;
  graniteGroundedness?: boolean;
  graniteAnswerRelevance?: boolean;
  graniteRiskDetected: boolean;
  graniteRagIssuesDetected: boolean;
  graniteRagExplanations: Record<string, { fix_response: string; fix_query_context: string }>;  // Explanations for RAG failures
  graniteCategoriesChecked: number;
  graniteCategoriesPassed: number;
  graniteCategoriesFailed: number;
  graniteOverallScore?: number;
  graniteRawResults: any;
  graniteModelUsed: string;
  
  // Embedding-based Tone & Groundedness Analysis (Step Level)
  embeddingAnalysisPerformed: boolean;
  embeddingAnalysis: any;
  embeddingGroundednessScore?: number;
  embeddingHallucinationScore?: number;
  embeddingToneScore?: number;
  embeddingGroundednessPassed?: boolean;
  embeddingHallucinationPassed?: boolean;
  embeddingTonePassed?: boolean;
  embeddingAllPassed?: boolean;
  embeddingIssuesDetected?: string[];
  embeddingModelUsed: string;
  embeddingBiasScore?: number;
  embeddingBiasPassed?: boolean;
  embeddingBiasExplanation?: string;
  embeddingBiasesFound?: string[];
  
  // LLM-based Semantic Analysis (more accurate than embeddings)
  llmSemanticAnalysisPerformed: boolean;
  llmSemanticAnalysis: any;
  llmGroundednessScore?: number;
  llmGroundednessPassed?: boolean;
  llmGroundednessExplanation?: string;
  llmUngroundedClaims?: string[];
  llmHallucinationScore?: number;
  llmHallucinationPassed?: boolean;
  llmHallucinationExplanation?: string;
  llmHallucinatedContent?: string[];
  llmToneScore?: number;
  llmTonePassed?: boolean;
  llmToneExplanation?: string;
  llmToneIssues?: string[];
  llmSemanticAllPassed?: boolean;
  llmSemanticIssuesSummary?: string;
  
  // Legacy fields for backward compatibility
  biasAnalysisPerformed: boolean;
  biasScore?: number;
  riskScore?: number;
  confidenceScore?: number;
  biasCategories: string[];
  riskCategories: string[];
  biasAnalysisDetails: string;
  errorMessage?: string;
  retryCount: number;
  
  // Detailed execution data
  preExecutionVariables: any;
  postExecutionVariables: any;
  extractedVariables: any;
  variableAssignmentsPerformed: any[];
  stepExecutionDetails: any;
  ifConditionResults: any[];
  loopIterationsData: any[];
  skipConditionResult: any;
  llmRequestsMade: any[];
  resolvedHeaders: any;
  resolvedBody: string;
  responseHeaders: any;
  responseBody: string;
  biasAnalysisPrompt: string;
  criteriaResults: any;
  resolvedAnalysisText?: string;
}

export interface SequenceExecutionResult {
  id: string;
  sequence: {
    id: string;
    name: string;
  };
  status: string;
  startedAt: string;
  completedAt?: string;
  totalSteps: number;
  completedSteps: number;
  failedSteps: number;
  
  // Llama Guard 3 harm category fields
  totalHarmCategoryPasses: number;
  totalHarmCategoryFailures: number;
  overallSafetyScore?: number;
  overallIsSafe: boolean;
  totalViolationsDetected: number;
  
  // Granite Guardian 3.0 aggregated fields
  graniteAnalysisPerformed: boolean;
  graniteTotalChecks: number;
  graniteTotalPassed: number;
  graniteTotalFailed: number;
  graniteOverallScore?: number;
  graniteRiskDetected: boolean;
  graniteRagIssuesDetected: boolean;
  graniteAnalysisResults: any[];
  
  // Embedding-based Tone & Groundedness Analysis
  embeddingAnalysis: any;
  embeddingToneDeviationScore?: number;
  embeddingGroundednessScore?: number;
  embeddingHallucinationScore?: number;
  embeddingToneShiftScore?: number;
  embeddingAllPassed: boolean;
  
  // Legacy fields for backward compatibility
  overallBiasScore?: number;
  overallRiskScore?: number;
  overallConfidenceScore?: number;
  violationsDetected: number;
  errorMessage?: string;
  stepExecutions: SequenceStepExecution[];
}
